import { produce } from 'immer';
import {
  OmniElement,
  OmniStyleElement,
  html,
  css,
  styleMap,
  OmniIconElement,
  nothing,
} from 'omni-ui';
import { Om2Dropdown } from 'omni-campaign-ui';
import './campaign-input-card.js';
import './campaign-draggable-chips.js';
import { EMPATHY_MAP_DEFAULT } from '../helpers/constants.js';

OmniStyleElement.register();
OmniIconElement.register();
Om2Dropdown.register();

/**
 * @summary Class for EmpathyMap Visualization Module Element.
 * @description Module is split into 4 Visualizations/quadrants where information can be stored as drag and drop chips within the respective quadrant they are added to.
 * The main way this is accomplished is by passing a string ("viz1", "viz2", "viz3", or "viz4") representing the quadrant to {@link _renderInputCard}
 * @extends OmniElement
 */
export class CampaignEmpathyMap extends OmniElement {
  /**
   * EmpathyMap Lit Properties
   * @property {String} env - Application environment
   * @property {Object} context - Values passed in from wrapper/router
   * @property {String} token - API JWT token
   * @property {Object[]} savedData - Data already saved in the API
   * @property {Object} quadrantData - Structure of data that gets sent to the API
   * @property {String} inputData - Handles state for input slotted into {@link CampaignInputCard}
   * @property {String} yAxisLabel - label used on the yAxisLabel passed in by the admin user. If the yAxisLabel key in the admin schema is left as an empty string it will render the standard EmpathyMap layout.
   */
  static get properties() {
    return {
      env: { type: String },
      context: { type: Object },
      token: { type: String },
      savedData: { type: Array },
      quadrantData: { type: Object },
      inputData: { type: String },
      yAxisLabel: { type: String, attribute: 'y-axis-label', reflect: true },
    };
  }

  /**
   * Empathy Map module styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        .omni .module-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: max-content 1fr 1fr max-content;
          grid-template-areas:
            'header header'
            'viz-1  viz-2'
            'viz-3  viz-4'
            'em-footer em-footer';
          gap: 0.143rem;
          width: 100%;
          height: 52.86rem;
          padding: 2.14rem 2.86rem 4.29rem 2.86rem;
        }
        /* Used to alter the layout depending if a string is passed to the reflected property yAxisLabel */
        :host([y-axis-label]) .omni .module-container {
          grid-template-columns: 2rem 1fr 1fr;
          grid-template-rows: max-content 1fr 1fr max-content;
          grid-template-areas:
            '.      header    header'
            'y-axis viz-1     viz-2'
            'y-axis viz-3     viz-4'
            '.      em-footer em-footer';
        }

        :host([y-axis-label]) .omni .y-axis {
          grid-area: y-axis;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        :host([y-axis-label]) .omni .y-axis p {
          flex: 1 1 auto;
          transform: rotate(-90deg);
          margin-bottom: 0.5rem;
        }

        .omni .header {
          grid-area: header;
          width: 100%;
        }

        .omni .sub-header {
          width: 100%;
        }

        .omni h2,
        .omni h3 {
          line-height: 1;
          color: rgb(var(--rgb-core-dark));
        }

        .omni .viz {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .omni div.left-input-toggle,
        .omni div.right-input-toggle {
          position: absolute;
          top: 1.43rem;
        }

        .omni div.left-input-toggle {
          left: 1.43rem;
        }

        .omni div.right-input-toggle {
          right: 4.57rem;
        }

        .omni h3.left-align,
        .omni h3.right-align {
          position: absolute;
          z-index: 3;
          bottom: 0.5rem;
        }

        .omni h3.left-align {
          left: 1.43rem;
        }

        .omni h3.right-align {
          right: 1.43rem;
        }

        .omni .viz1 {
          grid-area: viz-1;
        }
        .omni .viz2 {
          grid-area: viz-2;
        }
        .omni .viz3 {
          grid-area: viz-3;
        }
        .omni .viz4 {
          grid-area: viz-4;
        }

        .omni .em-footer {
          grid-area: em-footer;
        }

        campaign-input-card {
          position: absolute;
        }

        campaign-input-card::part(toggle-open-btn) {
          z-index: 3;
        }

        campaign-input-card::part(rendered-input) {
          margin: 2.143rem 0;
        }
      `,
    ];
  }

  /**
   * Initialize {@link #selectData},{@link #inputData}, {@link #backgroundColors} properties
   */
  constructor() {
    super();
    this.selectData = {};
    this.inputData = '';
    this.backgroundColors = {
      viz1: 'rgba(var(--rgb-primary), 0.1)',
      viz2: 'rgba(var(--rgb-primary), 0.4)',
      viz3: 'rgba(var(--rgb-primary), 0.2)',
      viz4: 'rgba(var(--rgb-primary), 0.3)',
    };
  }

  /**
   * Initialize module properties.
   * @property {Object} schema - passed by admin. Values for all the module's titles and labels
   */
  connectedCallback() {
    super.connectedCallback();
    this.quadrantData = {
      ...(this.savedData?.quadrantData || EMPATHY_MAP_DEFAULT.quadrantData),
    };
    this.moduleTitle ||= EMPATHY_MAP_DEFAULT.title;
    this.dropdownOptions = this._assignPropertiesFromSchema('dropdownOptions');
    this.subtitleLeft = this._assignPropertiesFromSchema('subtitle', 'left');
    this.subtitleMiddle = this._assignPropertiesFromSchema(
      'subtitle',
      'middle',
    );
    this.subtitleRight = this._assignPropertiesFromSchema('subtitle', 'right');
    this.middleLeft = this._assignPropertiesFromSchema('middle', 'left');
    this.middleRight = this._assignPropertiesFromSchema('middle', 'right');
    this.bottomMiddle = this._assignPropertiesFromSchema('bottom', 'middle');
    this.yAxisLabel = this._assignPropertiesFromSchema('yAxisLabel');
  }

  /**
   * Function that helps destructure values from schema. Defaults to EMPATHY_MAP_DEFAULT if value from schema is undefined
   * @param {String} child - first level of keys from schema
   * @param {String} grandChild - second level of keys within schema
   * @returns - value assigned to property
   */
  _assignPropertiesFromSchema = (child, grandChild) => {
    const config = this.schema?.[child] || EMPATHY_MAP_DEFAULT[child];
    return grandChild ? config?.[grandChild] : config;
  };

  /**
   * Function first updates the correct array within {@link #quadrantData} and then reassigns {@link #quadrantData} by spreading it into a new object to allow for strict comparison and Lit rerendering
   * @param {string} quadrant parameter to determine from which viz the information is coming from and to which key it should be saved within {@link #quadrantData}
   */
  _handleEmpathyData(quadrant) {
    const chip = {
      background: this.selectData.value,
      color: this.selectData.textColor,
      text: `${this.selectData.label[0]}: ${this.inputData}`,
      top: '20%',
      left: '20%',
    };
    this.quadrantData = produce(this.quadrantData, draft => {
      draft[quadrant] = [...draft[quadrant], chip];
    });
    this.selectData = {};
    this.inputData = '';
    this._handleChange();
  }

  /**
   * Fires an event that campaign-step-detail listens to letting it know that there has been a change in state/information.
   * We know the specific module the change occurred by passing the module uuid and how we want to save it, {@link _submitEmpathyData}, to the detail object.
   */
  _handleChange() {
    const { uuid_module: moduleId } = this.context;
    this.dispatchNewEvent('omni-module-changed', {
      detail: {
        moduleId,
        saveAction: this._submitEmpathyData.bind(this),
        moduleType: 'empathy map',
      },
    });
  }

  /**
   * Submits and saves the data to our backend API under the correct campaign and module.
   * Allows for the developer to structure the data as they see fit and stored in the APIs key savedData
   */
  async _submitEmpathyData() {
    await this.save({ quadrantData: this.quadrantData });
  }

  /**
   * Returns a html template literal of the configured {@link om2-dropdown} needed by {@link _renderInputCard}
   */
  _renderBulmaDropdown() {
    if (this.schema.yAxisLabel) {
      const selectData = this.dropdownOptions[0];
      this.selectData = selectData;
    }
    return this.schema.yAxisLabel
      ? html`
          <div class="field" style="margin-bottom: 0">
            <p class="control has-icons-right">
              <input
                class="input"
                ?disabled=${this.schema.yAxisLabel}
                .value=${this.selectData.label}
              />
              <span class="icon is-small is-right">
                <omni-icon icon-id="omni:interactive:down"></omni-icon>
              </span>
            </p>
          </div>
        `
      : html`
          <om2-dropdown
            required
            .options=${this.dropdownOptions}
            hasObjectValues
            idProperty="value"
            nameProperty="label"
            placeholder="Select keyword type"
            allowClear
            value=${this.selectData}
            @change=${e => {
              this.selectData = e.detail.value;
            }}
            class="dropDown"
            ?disabled=${this.schema.yAxisLabel}
          >
          </om2-dropdown>
        `;
  }

  /**
   *
   * @param {string} quadrant:to determine from which viz the information is coming from and to which key it should be saved within {@link #quadrantData}
   * @returns custom LIT element {@link #campaign-input-card} passing the quadrant specific data to the elements properties
   * @description render method that handles the quadrant specific customization needed for {@link #campaign-input-card}. Handles what the headers should be, the alignment of the input-card off its toggle button and how to save the data by passing down the quadrant parameter down to {@link #_handleEmpathyData}
   */
  _renderInputCard(quadrant) {
    const headerTitles = !this.yAxisLabel
      ? {
          viz1: `${this.middleLeft}/${this.subtitleMiddle}`,
          viz2: `${this.middleRight}/${this.subtitleMiddle}`,
          viz3: `${this.middleLeft}/${this.bottomMiddle}`,
          viz4: `${this.middleRight}/${this.bottomMiddle}`,
        }
      : {
          viz1: `${this.subtitleLeft}`,
          viz2: `${this.subtitleRight}`,
          viz3: `${this.middleLeft}`,
          viz4: `${this.middleRight}`,
        };

    const alignment = {
      viz1: null,
      viz2: 'right',
      viz3: null,
      viz4: 'right',
    };
    return html`
      ${this.context.readonly
        ? null
        : html`
            <campaign-input-card
              headerText=${headerTitles[quadrant]}
              tooltipText="Add Content"
              confirmBtnText="Add"
              .handleSubmit="${() => {
                this._handleEmpathyData(quadrant);
              }}"
              alignCard=${alignment[quadrant]}
              .bodyTemplate=${() => html`
                ${this._renderBulmaDropdown()}
                <input
                  required
                  part="rendered-input"
                  .value=${this.inputData}
                  @input=${e => {
                    this.inputData = e.target.value;
                  }}
                  class="input"
                  placeholder="Enter keyword or phrase"
                  }}
                />
              `}
            >
            </campaign-input-card>
          `}
    `;
  }

  /**
   *
   * @param {string} quadrant used to know which array we remove the object storing the chip data. Found in {@link #quadrantData} under the key that === the string referenced by the parameter quadrant
   * @param {number} ind used to know at which index to splice the object from the array
   * @description method that removes the chip from the data structure and the UI.
   */
  _removeChip(quadrant, ind) {
    this.quadrantData = produce(this.quadrantData, draft => {
      draft[quadrant].splice(ind, 1);
    });
    this._handleChange();
  }

  /**
   *
   * @param {string} quadrant used to know which array the object storing the chip data that needs to be updated is located in {@link #quadrantData}
   * @param {number} ind which index is the chip located in its respective array
   * @param {number} param2 chips top and left coordinates that need to be updated when the chip is dragged by the user
   * @description method that updates the top and left coordinates of the chip when it gets dragged
   */
  _updateChip(quadrant, ind, { top, left }) {
    this.quadrantData = produce(this.quadrantData, draft => {
      draft[quadrant][ind].top = top;
      draft[quadrant][ind].left = left;
    });
    this._handleChange();
  }

  /**
   *
   * @param {string} quadrant the array in {@link #quadrantData} the chip data is located
   * @returns <campaign-draggable-chips>
   * @description render method that passes the array data to render the chips in the correct quadrant. Also passes down the methods that handle deleting and updating chips
   */
  _renderDraggableChip(quadrant) {
    return html`
      <campaign-draggable-chips
        .chips=${this.quadrantData[quadrant]}
        .handleRemoveChip=${ind => {
          this._removeChip(quadrant, ind);
        }}
        .handleUpdateChip=${(ind, val) => {
          this._updateChip(quadrant, ind, val);
        }}
        .readonly=${this.context.readonly}
      >
      </campaign-draggable-chips>
    `;
  }

  /**
   *@description main html template where the two principle methods get called, {@link #_renderInputCard} and {@link #_renderDraggableChip}
   * @returns main html template for {@link #CampaignEmpathyMap}
   */
  render() {
    return html`
      <omni-style>
        <div class="box module-container">
          <div
            class="header is-flex is-flex-direction-column is-flex-wrap-nowrap"
          >
            <h2 class="title mb-5">${this.moduleTitle}</h2>
            <div
              class="sub-header is-flex is-justify-content-space-between mb-2"
            >
              <h2 class="has-text-weight-semibold">${this.subtitleLeft}</h2>
              ${this.yAxisLabel
                ? nothing
                : html`<h3>${this.subtitleMiddle}</h3>`}
              <h2 class="has-text-weight-semibold">${this.subtitleRight}</h2>
            </div>
          </div>
          <div class="y-axis"><p>${this.yAxisLabel}</p></div>
          <div
            class="viz viz1"
            style=${styleMap({
              backgroundColor: this.backgroundColors?.viz1,
            })}
          >
            <div class="left-input-toggle">
              ${this._renderInputCard('viz1')}
            </div>
            ${this._renderDraggableChip('viz1')}
            <h3 class="left-align">${this.middleLeft}</h3>
          </div>
          <div
            class="viz viz2"
            style=${styleMap({
              backgroundColor: this.backgroundColors?.viz2,
            })}
          >
            <div class="right-input-toggle">
              ${this._renderInputCard('viz2')}
            </div>
            ${this._renderDraggableChip('viz2')}
            <h3 class="right-align">${this.middleRight}</h3>
          </div>
          <div
            class="viz viz3"
            style=${styleMap({
              backgroundColor: this.backgroundColors?.viz3,
            })}
          >
            <div class="left-input-toggle">
              ${this._renderInputCard('viz3')}
            </div>
            ${this._renderDraggableChip('viz3')}
          </div>
          <div
            class="viz viz4"
            style=${styleMap({
              backgroundColor: this.backgroundColors?.viz4,
            })}
          >
            <div class="right-input-toggle">
              ${this._renderInputCard('viz4')}
            </div>
            ${this._renderDraggableChip('viz4')}
          </div>
          <div class="em-footer is-flex is-justify-content-center mt-2">
            <h3>${this.bottomMiddle}</h3>
          </div>
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-empathy-map', CampaignEmpathyMap);
